import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import React, { useState } from "react";
import { ScrollView, StyleSheet, TextInput, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";

import { colors, fonts, gradients, radius, shadows } from "@/lib/theme";

export interface TeachingItem {
    id: string;
    // The assessment section the teaching came from — the only header shown.
    section: string;
    body: string;
}

interface Props {
    items: TeachingItem[];
    onChangeItem: (id: string, body: string) => void;
    onDeleteItem: (id: string) => void;
    onRegenerate?: () => void;
}

export function TeachingReviewCards({ items, onChangeItem, onDeleteItem, onRegenerate }: Props) {
    return (
        <ScrollView contentContainerStyle={styles.scroll}>
            <View style={styles.topRow}>
                <View style={styles.countPill}>
                    <View style={styles.countDot} />
                    <Text style={styles.countText}>{items.length} items ready</Text>
                </View>
                <View style={{ flex: 1 }} />
                {onRegenerate ? (
                    <TouchableOpacity
                        onPress={onRegenerate}
                        activeOpacity={0.8}
                        style={styles.ghostBtn}
                    >
                        <Text style={styles.ghostBtnText}>Regenerate</Text>
                    </TouchableOpacity>
                ) : null}
            </View>

            {items.map((item) => (
                <TeachingCard
                    key={item.id}
                    item={item}
                    onSave={(body) => onChangeItem(item.id, body)}
                    onDelete={() => onDeleteItem(item.id)}
                />
            ))}
        </ScrollView>
    );
}

interface CardProps {
    item: TeachingItem;
    onSave: (body: string) => void;
    onDelete: () => void;
}

function TeachingCard({ item, onSave, onDelete }: CardProps) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(item.body);

    const startEdit = () => {
        setDraft(item.body);
        setEditing(true);
    };
    const save = () => {
        onSave(draft.trim());
        setEditing(false);
    };

    return (
        <View style={styles.card}>
            <View style={styles.cardHeader}>
                <Text style={styles.section}>{item.section}</Text>
                <View style={{ flex: 1 }} />

                {editing ? (
                    <TouchableOpacity onPress={save} activeOpacity={0.85}>
                        <LinearGradient
                            colors={[...gradients.brand]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 1 }}
                            style={styles.saveBtn}
                        >
                            <MaterialCommunityIcons name="check" size={13} color="#FFFFFF" />
                            <Text style={styles.saveBtnText}>Save</Text>
                        </LinearGradient>
                    </TouchableOpacity>
                ) : (
                    <TouchableOpacity
                        onPress={startEdit}
                        activeOpacity={0.8}
                        style={styles.editBtn}
                    >
                        <MaterialCommunityIcons
                            name="pencil-outline"
                            size={13}
                            color={colors.textSecondary}
                        />
                        <Text style={styles.editBtnText}>Edit</Text>
                    </TouchableOpacity>
                )}

                <TouchableOpacity
                    onPress={onDelete}
                    activeOpacity={0.8}
                    style={styles.trashBtn}
                    accessibilityLabel="Delete teaching item"
                >
                    <MaterialCommunityIcons
                        name="trash-can-outline"
                        size={15}
                        color={colors.gray500}
                    />
                </TouchableOpacity>
            </View>

            {editing ? (
                <TextInput
                    value={draft}
                    onChangeText={setDraft}
                    multiline
                    autoFocus
                    style={styles.input}
                    placeholderTextColor={colors.textTertiary}
                />
            ) : (
                <Text style={styles.body}>{item.body}</Text>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    scroll: {
        paddingHorizontal: 16,
        paddingTop: 18,
        paddingBottom: 24,
        gap: 10
    },
    topRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginBottom: 4
    },
    countPill: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingHorizontal: 11,
        paddingVertical: 5,
        borderRadius: radius.full,
        backgroundColor: colors.teal50
    },
    countDot: {
        width: 5,
        height: 5,
        borderRadius: 2.5,
        backgroundColor: colors.teal400
    },
    countText: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.teal700,
        letterSpacing: 1.1,
        textTransform: "uppercase"
    },
    ghostBtn: {
        borderWidth: 1,
        borderColor: colors.gray200,
        backgroundColor: colors.surface,
        borderRadius: radius.full,
        paddingHorizontal: 12,
        paddingVertical: 6
    },
    ghostBtnText: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.textSecondary
    },
    card: {
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.lg,
        backgroundColor: colors.surface,
        paddingHorizontal: 16,
        paddingVertical: 14,
        ...shadows.sm
    },
    cardHeader: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginBottom: 8
    },
    section: {
        fontSize: 10,
        fontFamily: fonts.bold,
        color: colors.textTertiary,
        letterSpacing: 1,
        textTransform: "uppercase"
    },
    editBtn: {
        flexDirection: "row",
        alignItems: "center",
        gap: 5,
        borderWidth: 1,
        borderColor: colors.gray200,
        borderRadius: radius.full,
        paddingHorizontal: 12,
        paddingVertical: 6
    },
    editBtnText: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.textSecondary
    },
    saveBtn: {
        flexDirection: "row",
        alignItems: "center",
        gap: 5,
        borderRadius: radius.full,
        paddingHorizontal: 13,
        paddingVertical: 7
    },
    saveBtnText: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: "#FFFFFF"
    },
    trashBtn: {
        width: 28,
        height: 28,
        borderRadius: radius.full,
        borderWidth: 1,
        borderColor: colors.gray200,
        alignItems: "center",
        justifyContent: "center"
    },
    body: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        lineHeight: 19
    },
    input: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.textPrimary,
        lineHeight: 19,
        minHeight: 96,
        borderWidth: 1,
        borderColor: colors.teal200,
        borderRadius: radius.sm,
        backgroundColor: colors.teal50,
        paddingHorizontal: 12,
        paddingVertical: 10,
        textAlignVertical: "top"
    }
});
